import { resolveConfig } from './config';
import { getHalachicDate } from './helpers/sunsetDetection';
import { getCalendarData, computeShabbatTitle } from './providers/calendarProvider';
import { getZmanimData } from './providers/zmanimProvider';
import { getLeyningData } from './providers/leyningProvider';
import { getDafYomiData, getAllLimudimData } from './providers/learningProvider';
import { cacheKey, getCached, setCached } from './cache/engineCache';

import { computeTachanun } from './halacha/tachanunRules';
import { computeHallel } from './halacha/hallelRules';
import { computePrayerAdditions } from './halacha/prayerAdditions';
import { computeSeasonalPrayers } from './halacha/seasonalPrayers';
import { computeShabbatAdditions } from './halacha/shabbatAdditions';
import { computeSeasonalCustoms } from './halacha/seasonalCustoms';
import { computeSelichot } from './halacha/selichotRules';
import { computeMotzaeiShabbat } from './halacha/motzaeiShabbatRules';
import { computeWeekdayPrayers } from './halacha/weekdayPrayers';
import { computeLiturgy } from './halacha/liturgyRules';
import { computeNidcheh } from './halacha/nidchehRules';
import { computeMourningPeriods } from './halacha/mourningPeriods';

/**
 * Main Halachic Calendar Engine.
 *
 * Computes a complete HalachicDayPayload for a given date and configuration.
 * Results are cached by location + halachic date.
 */
export class HalachicEngine {
  /**
   * @param {object} userConfig - partial EngineConfig
   */
  constructor(userConfig = {}) {
    this.config = resolveConfig(userConfig);
  }

  /**
   * Compute the full halachic payload for a specific date.
   *
   * @param {Date} [date=new Date()] - civil JS Date
   * @param {object} [options] - { skipZmanim: false, skipLeyning: false }
   * @returns {Promise<object>} HalachicDayPayload
   */
  async compute(date = new Date(), options = {}) {
    const { location, isIsrael, edah } = this.config;

    // Step 0: Determine halachic date (advance if after sunset)
    const { hdate, isAfterSunset } = getHalachicDate(date, location);
    const dateKey = `${hdate.getFullYear()}-${hdate.getMonth()}-${hdate.getDate()}`;

    // Check cache
    const key = cacheKey(location.lat, location.lng, dateKey);
    const cached = getCached(key);
    if (cached) return cached;

    // Step 1: Calendar data (HDate, holidays, parsha, events)
    const calData = getCalendarData(hdate, isIsrael);

    // Step 2: Zmanim (REST — async)
    const civilDateStr = this._toCivilDateStr(date, isAfterSunset);
    let zmanim = null;
    if (!options.skipZmanim) {
      try {
        zmanim = await getZmanimData(
          { lat: location.lat, lng: location.lng },
          civilDateStr
        );
      } catch (err) {
        console.warn('Zmanim fetch failed:', err.message);
      }
    }

    // Step 3: Leyning
    let leyning = null;
    if (!options.skipLeyning) {
      leyning = getLeyningData(hdate, isIsrael);
    }

    // Step 4: Daf Yomi + all daily learning programs
    const dafYomi = getDafYomiData(hdate);
    const limudim = getAllLimudimData(hdate, isIsrael);

    // Step 5: Halachic Logic Layer
    const nidcheh = computeNidcheh(hdate, calData.holidays);
    const mourningPeriod = computeMourningPeriods(hdate);
    const tachanun = computeTachanun(hdate, isIsrael, calData);
    const hallel = computeHallel(hdate, isIsrael, this.config);
    const prayerAdditions = computePrayerAdditions(hdate, calData);
    const seasonalPrayers = computeSeasonalPrayers(hdate, isIsrael, edah, date);
    const shabbatAdditions = computeShabbatAdditions(hdate, calData, tachanun, edah);
    const seasonalCustoms = computeSeasonalCustoms(hdate, isIsrael, calData);
    const selichot = computeSelichot(hdate, edah);
    const motzaeiShabbat = computeMotzaeiShabbat(hdate, isIsrael);
    const weekdayPrayers = computeWeekdayPrayers(hdate, calData, tachanun, edah);
    const liturgy = computeLiturgy(hdate, calData, isIsrael, edah);

    // Step 6: Convenience fields
    const { shabbatTitle, shabbatTitleType } = computeShabbatTitle(calData);

    // Compose payload
    const payload = {
      // Identity
      date: civilDateStr,
      hebrewDate: calData.hebrewDate,

      // Calendar status
      parsha: calData.parsha,
      holidays: calData.holidays,
      isShabbat: calData.isShabbat,
      isYomTov: calData.isYomTov,
      isCholHamoed: calData.isCholHamoed,
      isRoshChodesh: calData.isRoshChodesh,
      isFastDay: calData.isFastDay,
      isIsruChag: calData.isIsruChag,
      isChanukah: calData.isChanukah,
      specialShabbat: calData.specialShabbat,

      // Convenience display
      shabbatTitle,
      shabbatTitleType,

      // Zmanim
      zmanim,

      // Torah readings
      leyning,
      dafYomi,
      limudim,

      // Halachic flags
      mourningPeriod,
      tachanun,
      prayerAdditions,
      hallel,
      seasonalPrayers,
      shabbatAdditions,
      seasonalCustoms,
      selichot,
      motzaeiShabbat,
      weekdayPrayers,
      liturgy,

      // Metadata
      isAfterSunset,
      computedAt: Date.now(),
      configHash: this._configHash(),
    };

    setCached(key, payload);
    return payload;
  }

  /**
   * Compute payloads for today and tomorrow.
   * @param {Date} [now=new Date()]
   * @returns {Promise<{ today: object, tomorrow: object }>}
   */
  async computeTodayAndTomorrow(now = new Date()) {
    const today = await this.compute(now);
    const tomorrowDate = new Date(now);
    tomorrowDate.setDate(tomorrowDate.getDate() + 1);
    tomorrowDate.setHours(12, 0, 0, 0); // Noon to avoid sunset ambiguity
    const tomorrow = await this.compute(tomorrowDate);
    return { today, tomorrow };
  }

  /**
   * Compute payloads for a range of days (for lookahead features).
   * Uses skipZmanim to avoid unnecessary API calls for future days.
   *
   * @param {Date} [startDate=new Date()] - starting civil date
   * @param {number} [daysCount=7] - number of days to compute
   * @param {object} [options] - override options per day
   * @returns {Promise<object[]>} array of HalachicDayPayloads
   */
  async computeRange(startDate = new Date(), daysCount = 7, options = {}) {
    const results = [];
    for (let i = 0; i < daysCount; i++) {
      const d = new Date(startDate);
      d.setDate(d.getDate() + i);
      d.setHours(12, 0, 0, 0); // Noon to avoid sunset ambiguity
      const dayOptions = i === 0
        ? options // First day: use caller's options (may include zmanim)
        : { ...options, skipZmanim: true }; // Future days: skip API calls
      const payload = await this.compute(d, dayOptions);
      results.push(payload);
    }
    return results;
  }

  /**
   * Update engine configuration.
   * @param {object} newConfig
   */
  updateConfig(newConfig) {
    this.config = resolveConfig(newConfig);
  }

  /**
   * Convert JS Date to civil date string for the halachic day.
   * If after sunset, the zmanim date should be tomorrow's civil date.
   */
  _toCivilDateStr(date, isAfterSunset) {
    const d = new Date(date);
    if (isAfterSunset) {
      d.setDate(d.getDate() + 1);
    }
    // Use local date parts (not toISOString which returns UTC and can
    // shift the date backwards when local time is between midnight and
    // the UTC offset, e.g. 00:00–03:00 in Israel).
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  _configHash() {
    const { location, isIsrael, edah } = this.config;
    return `${location.lat}|${location.lng}|${isIsrael}|${edah}`;
  }
}
